import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { gbpShort } from "../lib/format";
import { ErrorState, Loading, PageHeader, Panel, VatNote } from "../components/common";
import { KpiCard } from "../components/KpiCard";
import { WaitingTray } from "../components/WaitingTray";
import { OtherApprovals } from "../components/OtherApprovals";
import { YearQuarters, WhoGetsWhat } from "../components/YearSummary";
import { CashChart } from "../components/CashChart";
import { AlertsPanel } from "../components/AlertsPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function QuickFind() {
  const { data } = useApi("/projects", { live: false });
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const hits = q.length < 2 ? [] : (data?.rows || []).filter((r) => `${r.name} ${r.partner_name} ${r.tab_name}`.toLowerCase().includes(q.toLowerCase())).slice(0, 6);
  return (
    <div className="relative w-full sm:w-80">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a project…" data-testid="dashboard-project-search"
        className="h-10 w-full rounded-full border bg-card pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary" />
      {hits.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-lg">
          {hits.map((h) => (
            <li key={h.id}>
              <button onClick={() => navigate(`/projects/${h.id}`)} className="block w-full px-3 py-2 text-left text-sm hover:bg-muted" data-testid={`search-result-${h.ref}`}>
                <div className="truncate font-semibold">{h.name}</div><div className="text-xs text-muted-foreground">{h.partner_name}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [year, setYear] = useState(Math.min(2031, Math.max(2016, new Date().getFullYear())));
  const [tft, setTft] = useState(true);
  const { data, error, status, loading, reload } = useApi("/dashboard", { params: { year, include_tft: tft } });
  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorState message={error} status={status} />;
  const k = data.kpis;
  const ys = data.year_summary;
  const first = user.name.split(" ")[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  return (
    <div className="space-y-6 lg:space-y-8" data-testid="dashboard-page">
      <PageHeader eyebrow="Portfolio dashboard" title={`${greet}, ${first}`}
        subtitle={`${data.kpis.projects_total} JV projects · report window ${data.report_window.start} – ${data.report_window.end}`}
        actions={<>
          <QuickFind />
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold" data-testid="include-tft-label"><input type="checkbox" checked={tft} onChange={(e) => setTft(e.target.checked)} data-testid="include-tft" />Include TFT</label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32 h-10 rounded-full" data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{data.years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`year-option-${y}`}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </>} />

      <WaitingTray items={data.waiting} approver={user.roles.includes("Approver")} onChange={() => reload(true)} />
      <OtherApprovals items={data.waiting_other} onChange={() => reload(true)} />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
        <KpiCard testid="kpi-projects-live" label="Projects live" value={k.projects_live} display={k.projects_live} delay={0}
          hint={`${data.status_counts.Pipeline} pipeline · ${data.status_counts.Exited} exited · ${k.projects_total} in total`} />
        <KpiCard testid="kpi-equity-deployed" label="Equity deployed" value={k.deployed} delay={60} hint={`of ${gbpShort(k.current_equity)} equity requirement (current) · of which loan ${gbpShort(k.of_which_loan || 0)}`} />
        <KpiCard testid="kpi-equity-to-deploy" label="Equity to deploy" value={k.to_deploy} delay={120} hint="equity requirement minus equity deployed" />
        <KpiCard testid="kpi-equity-returned" label="Equity returned" value={k.equity_returned} delay={180} hint={`all quarters · Cash in ${gbpShort(k.equity_returned + k.profit)} (equity returned + profit)`} tone="pos" />
        <KpiCard testid="kpi-profit" label="Forecast profit" value={k.profit} delay={240} hint="EPIL profit in the cashflows" tone="pos" />
        <KpiCard testid="kpi-afzal-net-position" label={`Afzal net position · ${year}`} value={k.afzal_net} delay={300} accent
          tone={k.afzal_net < 0 ? "neg" : "pos"} hint={k.afzal_net < 0 ? "more going in than coming back this year" : "more coming back than going in this year"} />
      </div>

      <VatNote testid="dashboard-vat-note" className="-mt-2" />
      <div className="rise rounded-lg border-l-4 border-primary bg-card px-5 py-5 md:px-7 md:py-6" data-testid="year-summary-sentence">
        <div className="eyebrow mb-2">In plain English</div>
        <p className="font-heading text-lg md:text-xl font-semibold leading-snug">{ys.sentence}</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Panel eyebrow="Afzal's equity requirement and cash in" title={`Year Summary · ${year}`} testid="year-summary-panel">
          <YearQuarters ys={ys} />
          <div className="mt-8"><WhoGetsWhat ys={ys} /></div>
        </Panel>
        <AlertsPanel alerts={data.alerts} count={data.alerts_count} />
      </div>

      <Panel eyebrow="All projects (100%)" title={`Quarterly cash · ${data.report_window.start} – ${data.report_window.end}`} testid="cash-chart-panel">
        <div className="w-full min-w-0 overflow-hidden"><CashChart data={data.chart} /></div>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="cash-chart-caption">So far {gbpShort(k.deployed)} equity has been deployed; {gbpShort(k.to_deploy)} is the equity to deploy. Bars below the line are equity deployed; net = equity deployed + cash in for the quarter.</p>
      </Panel>
    </div>
  );
}
